import React, { useEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { X, Download, Share2, Copy, Sparkles } from "lucide-react";
import { formatINR, formatShortINR } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Viral "Food Year in Review" shareable card.
 * Renders a beautiful 1080x1350 (Instagram story-ish) card using inline styles
 * so html-to-image can capture it pixel-perfect regardless of tailwind classes.
 *
 * Now includes a referral footer ("Try free → cartiq360.app/?ref=XXXX")
 * so the highest-virality moment doubles as a growth channel.
 */
export default function ShareableCard({ report, onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [referral, setReferral] = useState(null);

  // Best-effort: fetch the user's referral link so we can stamp it on the
  // card. Gracefully degrades for anonymous users (we just show the bare
  // domain with no `?ref=` tag).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/referrals/me`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setReferral(data);
      } catch {
        /* anonymous viewer — ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build the referral URL up-front (must be before any early-return so the
  // hook order remains stable across renders).
  const referralUrl = useMemo(() => {
    if (!referral?.code) return referral?.shareUrl || "";
    if (typeof window === "undefined") return referral.shareUrl || "";
    return `${window.location.origin}/?ref=${referral.code}`;
  }, [referral?.code, referral?.shareUrl]);

  if (!report) return null;

  const summary = report.summary || {};
  const topCat = (report.categoryBreakdown || [])[0] || { category: "—", percentage: 0 };
  const topPlat = (report.platformBreakdown || [])[0] || { platform: "—", percentage: 0 };
  const topItem = (report.topItems || [])[0] || { item: "—", timesOrdered: 0 };
  const yearlyProjection = (summary.totalSpend || 0) * 12;

  const referralCode = referral?.code || "";
  const cardCtaUrl = referralUrl
    ? referralUrl.replace(/^https?:\/\//, "")
    : "cartiq360.solutionist.co.in";
  const shareText = referralUrl
    ? `I finally saw where my ₹${formatShortINR(summary.totalSpend || 0).replace("₹", "")}/month goes 😳 Get 1 month of Pro free with my link → ${referralUrl}`
    : "I finally saw where my ₹18,000/month goes. 😳 You should too.";

  const exportPng = async () => {
    if (!cardRef.current) return null;
    try {
      return await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0A0A0F",
      });
    } catch (e) {
      console.error("toPng failed", e);
      toast.error("Could not generate image.");
      return null;
    }
  };

  const onDownload = async () => {
    setBusy(true);
    const dataUrl = await exportPng();
    if (dataUrl) {
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `cartiq360-food-year-in-review.png`;
      a.click();
      toast.success("Saved to your device.");
    }
    setBusy(false);
  };

  const onShare = async () => {
    setBusy(true);
    const dataUrl = await exportPng();
    if (!dataUrl) {
      setBusy(false);
      return;
    }
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "cartiq360-food-year.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Food Year — CartIQ 360",
          text: shareText,
          ...(referralUrl ? { url: referralUrl } : {}),
        });
        toast.success("Shared!");
      } else {
        // Fallback: download
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `cartiq360-food-year-in-review.png`;
        a.click();
        toast.info("Sharing not supported — downloaded instead.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error(e);
        toast.error("Could not share.");
      }
    }
    setBusy(false);
  };

  const onCopyLink = async () => {
    try {
      // Prefer the referral link so every share funnels back to a credited
      // signup. Falls back to the current page URL for anonymous viewers.
      const target = referralUrl || window.location.href;
      await navigator.clipboard.writeText(target);
      toast.success(referralUrl ? "Referral link copied." : "Dashboard link copied.");
    } catch {
      toast.error("Could not copy.");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[#0A0A0F]/95 backdrop-blur-sm z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid="shareable-card-modal"
      onClick={onClose}
    >
      <div
        className="max-w-lg w-full my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3 text-[#F0EDE8]">
          <div className="flex items-center gap-2 text-sm uppercase tracking-[0.22em]">
            <Sparkles className="w-4 h-4 text-[#FF6B00]" />
            Your shareable card
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center border border-[#2A2A35] hover:border-[#FF6B00] hover:text-[#FF6B00]"
            aria-label="Close"
            data-testid="shareable-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* The actual card — captured by html-to-image */}
        <div
          ref={cardRef}
          data-testid="shareable-card"
          style={{
            width: "100%",
            aspectRatio: "4 / 5",
            background: "#0A0A0F",
            color: "#F0EDE8",
            fontFamily: "'Manrope', system-ui, sans-serif",
            position: "relative",
            overflow: "hidden",
            border: "1px solid #2A2A35",
          }}
        >
          {/* Top stripe */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "6px",
              background: "linear-gradient(90deg,#FF6B00 0%,#FF6B00 70%,transparent 100%)",
            }}
          />

          {/* Hero */}
          <div style={{ padding: "28px 28px 0" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  background: "#FF6B00",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'Anton',Impact,sans-serif",
                  color: "#fff",
                  fontSize: "18px",
                  lineHeight: 1,
                }}
              >
                C
              </div>
              <div
                style={{
                  fontFamily: "'Anton',Impact,sans-serif",
                  fontSize: "18px",
                  letterSpacing: "-0.02em",
                  textTransform: "uppercase",
                }}
              >
                CartIQ <span style={{ color: "#FF6B00" }}>360</span>
              </div>
              <div
                style={{
                  marginLeft: "auto",
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.22em",
                  color: "#888",
                }}
              >
                Food Year · Review
              </div>
            </div>

            <div
              style={{
                fontFamily: "'Georgia',serif",
                fontSize: "28px",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                marginBottom: "6px",
              }}
            >
              I finally saw where
              <br />
              <span style={{ color: "#E24B4A" }}>₹{formatShortINR(summary.totalSpend || 0).replace("₹", "")}</span>{" "}
              <span style={{ color: "#888" }}>a month goes.</span>
            </div>
            <div
              style={{
                fontFamily: "'Georgia',serif",
                fontStyle: "italic",
                fontSize: "14px",
                color: "#888",
              }}
            >
              across {summary.ordersAnalysed || 0} orders on {topPlat.platform}, {(report.platformBreakdown || []).length} platforms
            </div>
          </div>

          {/* Stats grid */}
          <div
            style={{
              padding: "24px 28px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              marginTop: "14px",
            }}
          >
            <StatBlock
              label="This month"
              value={formatINR(summary.totalSpend || 0)}
              accent="#FF6B00"
            />
            <StatBlock
              label="Per year, at this rate"
              value={formatShortINR(yearlyProjection)}
              accent="#E24B4A"
            />
            <StatBlock
              label="Savings unlocked"
              value={formatINR(summary.potentialSavings || 0)}
              accent="#1D9E75"
            />
            <StatBlock
              label="Top category"
              value={topCat.category}
              valueFont="'Anton',Impact,sans-serif"
              uppercaseValue
              accent="#378ADD"
            />
          </div>

          {/* Insight strip */}
          <div
            style={{
              margin: "4px 28px 0",
              background: "#1E1E2E",
              borderLeft: "3px solid #FF6B00",
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                fontSize: "10px",
                textTransform: "uppercase",
                letterSpacing: "0.22em",
                color: "#888",
                marginBottom: "4px",
              }}
            >
              Most-bought item
            </div>
            <div style={{ fontSize: "17px", fontWeight: 700 }}>
              {topItem.item}
            </div>
            <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
              ordered {topItem.timesOrdered} times in the last 30 days
            </div>
          </div>

          {/* CTA */}
          <div
            style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              right: "0",
              padding: "16px 28px 22px",
              borderTop: "1px solid #2A2A35",
              background: "#0A0A0F",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: "'Anton',Impact,sans-serif",
                    fontSize: "18px",
                    textTransform: "uppercase",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Know how you <span style={{ color: "#FF6B00" }}>eat.</span>
                </div>
                <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                  {cardCtaUrl}
                </div>
                {referralCode && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#1D9E75",
                      marginTop: "4px",
                      textTransform: "uppercase",
                      letterSpacing: "0.18em",
                      fontWeight: 700,
                    }}
                  >
                    1 month of Pro free · code {referralCode}
                  </div>
                )}
              </div>
              <div
                style={{
                  background: "#FF6B00",
                  color: "#fff",
                  padding: "10px 16px",
                  fontWeight: 800,
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                }}
              >
                Try Free →
              </div>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="mt-4 grid grid-cols-3 gap-2" data-testid="shareable-actions">
          <button
            type="button"
            className="btn-ghost flex items-center justify-center gap-2"
            onClick={onShare}
            disabled={busy}
            data-testid="shareable-share-btn"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button
            type="button"
            className="btn-ghost flex items-center justify-center gap-2"
            onClick={onDownload}
            disabled={busy}
            data-testid="shareable-download-btn"
          >
            <Download className="w-4 h-4" /> Save
          </button>
          <button
            type="button"
            className="btn-ghost flex items-center justify-center gap-2"
            onClick={onCopyLink}
            data-testid="shareable-copy-btn"
          >
            <Copy className="w-4 h-4" /> Link
          </button>
        </div>
        <div className="text-xs text-[#555] text-center mt-3 tracking-[0.12em] uppercase" data-testid="shareable-help">
          {referralCode
            ? "Every signup via your link = 1 month of Pro free for you."
            : "Share on WhatsApp, Instagram Story, X — drive the curious to their own report."}
        </div>
      </div>
    </div>
  );
}

const StatBlock = ({ label, value, accent, valueFont, uppercaseValue }) => (
  <div
    style={{
      background: "#1E1E2E",
      padding: "14px 16px",
      borderTop: `2px solid ${accent}`,
    }}
  >
    <div
      style={{
        fontSize: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.22em",
        color: "#888",
        marginBottom: "6px",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: valueFont || "'JetBrains Mono',monospace",
        fontSize: "22px",
        fontWeight: 700,
        letterSpacing: "-0.02em",
        color: accent,
        textTransform: uppercaseValue ? "uppercase" : "none",
      }}
    >
      {value}
    </div>
  </div>
);
